import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components';
import { CompanyLegalDetails } from '../components/CompanyLegalDetails';
import { ContactForm } from '../components/ContactForm';
import { HeroImagePager } from '../components/HeroImagePager';

/** Hero images, as on etappio.se; each has a class .landing-hero--N in App.css. */
const HERO_IMAGE_COUNT = 6;

export function Landing() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [heroImage, setHeroImage] = useState(0);
  const translationList = (key: string) => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? value : [];
  };

  // The header overlays the hero transparently at the top of the page and turns
  // into a solid white bar once the visitor scrolls past the hero edge (matches
  // the Redesign/Home reference). Scoped to this logged-out page only.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setMobileMenuOpen(false);
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Solid header when scrolled, or when the mobile menu is expanded (its links
  // need a readable opaque background regardless of scroll position).
  const headerSolid = scrolled || mobileMenuOpen;

  return (
    <div className="landing-page min-vh-100 d-flex flex-column">
      {/* Header */}
      <div className="landing-header-slot position-sticky top-0 flex-shrink-0">
      <header
        className={`landing-header py-3 px-4 ${headerSolid ? 'bg-white border-bottom shadow-sm' : 'landing-header-transparent'}`}
      >
        <div className="container">
          <div className="d-flex align-items-center justify-content-between">
            <a
              href="#top"
              className="d-flex align-items-center text-decoration-none"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              <img
                src="/assets/images/logo-full.png"
                alt="ETAPPIO"
                height="40"
                style={{
                  filter: headerSolid ? 'none' : 'brightness(0) invert(1)',
                  transition: 'filter .3s ease'
                }}
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="d-none d-md-flex align-items-center gap-4">
              <a
                href="https://www.etappio.se"
                className={`text-decoration-none ${headerSolid ? 'text-dark' : 'text-white'}`}
                style={{ fontWeight: 500, fontSize: '1rem' }}
              >
                Etappio.se
              </a>
              <a
                href="#contact"
                className={`text-decoration-none ${headerSolid ? 'text-dark' : 'text-white'}`}
                style={{ fontWeight: 500, fontSize: '1rem' }}
                onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
              >
                {t('landing.nav.contact')}
              </a>
              <LanguageSwitcher />
              <Link to="/login" className={`btn ${headerSolid ? 'btn-light-brand' : 'btn-outline-light'}`}>
                {t('nav.login')}
              </Link>
            </nav>

            {/* Mobile Menu Button */}
            <div className="d-flex d-md-none align-items-center gap-2">
              <LanguageSwitcher />
              <button
                className={`btn btn-link p-2 ${headerSolid ? 'text-dark' : 'text-white'}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                <i className={`feather-${mobileMenuOpen ? 'x' : 'menu'} fs-4`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="d-md-none pt-3 pb-2 border-top mt-3">
              <div className="d-flex flex-column gap-3">
                <a
                  href="https://www.etappio.se"
                  className="text-decoration-none text-dark py-2"
                  style={{ fontWeight: 500, fontSize: '1rem' }}
                >
                  Etappio.se
                </a>
                <a
                  href="#contact"
                  className="text-decoration-none text-dark py-2"
                  style={{ fontWeight: 500, fontSize: '1rem' }}
                  onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
                >
                  {t('landing.nav.contact')}
                </a>
                <Link to="/login" className="btn btn-light-brand w-100">
                  {t('nav.login')}
                </Link>
              </div>
            </nav>
          )}
        </div>
      </header>
      </div>

      {/* Hero Section */}
      {/* The same renovation images as etappio.se, browsable, labelled as
          AI-generated (ClickUp 869fkgc63). Only the active image's class is in
          the DOM, so the others load when shown. */}
      <section className={`landing-hero landing-hero--${heroImage + 1} flex-grow-1 d-flex align-items-center text-start`}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <h1 className="fw-bold text-uppercase text-white mb-4" style={{ fontSize: 'clamp(40px, 3.2vw, 64px)', lineHeight: 1.15, textAlign: 'left' }}>
                {t('landing.heroTitle')} <span style={{ color: '#229954' }}>{t('landing.heroSubtitle')}</span>
              </h1>
              <p
                className="mb-4"
                style={{ fontSize: 'clamp(18px, 1.1vw, 22px)', fontWeight: 300, color: 'rgba(255,255,255,0.88)', maxWidth: '620px' }}
              >
                {t('landing.heroDescription')}
              </p>
              <a
                href="#register-options"
                className="d-inline-block fw-bold text-uppercase mb-3 text-decoration-none"
                style={{ color: '#27AE60', letterSpacing: '0.15em', fontSize: '0.85rem' }}
                onClick={(e) => { e.preventDefault(); scrollToSection('register-options'); }}
              >
                {t('landing.heroCtaLabel')}
              </a>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-start">
                <a
                  href="#register-options"
                  className="btn px-4 py-3 fw-bold rounded-pill d-inline-flex align-items-center justify-content-center"
                  style={{ backgroundColor: '#fff', color: '#1E3A5F', border: 'none', textTransform: 'none', fontSize: '1rem' }}
                  onClick={(e) => { e.preventDefault(); scrollToSection('register-options'); }}
                >
                  <i className="bi bi-building me-2" style={{ color: '#2ECC71', fontSize: '1.15rem' }}></i>
                  {t('landing.heroCtaCompany')}
                </a>
                <a
                  href="#register-options"
                  className="btn px-4 py-3 text-white fw-bold rounded-pill d-inline-flex align-items-center justify-content-center"
                  style={{ backgroundColor: 'rgba(23, 32, 42, 0.45)', border: '1.5px solid rgba(255,255,255,0.6)', textTransform: 'none', fontSize: '1rem' }}
                  onClick={(e) => { e.preventDefault(); scrollToSection('register-options'); }}
                >
                  <i className="bi bi-person-circle me-2" style={{ color: '#2ECC71', fontSize: '1.15rem' }}></i>
                  {t('landing.heroCtaPerson')}
                </a>
              </div>
            </div>
          </div>
        </div>
        <a
          href="#register-options"
          className="d-none d-md-flex flex-column align-items-center text-decoration-none text-white"
          style={{ position: 'absolute', bottom: '1.75rem', left: '50%', transform: 'translateX(-50%)' }}
          onClick={(e) => { e.preventDefault(); scrollToSection('register-options'); }}
        >
          <span className="fw-semibold text-uppercase mb-1" style={{ fontSize: '0.8rem', letterSpacing: '2px' }}>{t('landing.heroScrollDown')}</span>
          <i className="feather-chevron-down" style={{ fontSize: '1.6rem', lineHeight: 1 }}></i>
        </a>
        <p className="landing-hero-ai-label">{t('landing.heroAiLabel')}</p>
        <HeroImagePager index={heroImage} count={HERO_IMAGE_COUNT} onChange={setHeroImage} />
      </section>

      {/* Register options: one service for both parties */}
      <section id="register-options" style={{ background: '#f5f6f8', paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <div className="container">
          <div className="text-start mb-4">
            <h2 className="fw-bold mb-2" style={{ color: '#1E3A5F' }}>{t('landing.registerOptions.title')}</h2>
            <p className="text-muted mb-0 me-auto" style={{ maxWidth: '620px' }}>{t('landing.registerOptions.subtitle')}</p>
          </div>
          <div className="row g-4">
            {/* Byggföretag */}
            <div className="col-md-6">
              <div className="h-100 bg-white rounded-4 p-4 d-flex flex-column border">
                <i className="bi bi-building mb-3" style={{ color: '#1E3A5F', fontSize: '1.9rem' }}></i>
                <h3 className="h4 fw-bold mb-2">{t('landing.registerOptions.company.title')}</h3>
                <p className="text-muted mb-3">{t('landing.registerOptions.company.description')}</p>
                <ul className="list-unstyled mb-4">
                  {translationList('landing.registerOptions.company.points').map((point, i) => (
                    <li key={i} className="d-flex align-items-start gap-2 mb-2">
                      <i className="feather-check-circle flex-shrink-0" style={{ color: '#2ECC71', marginTop: '2px' }}></i>
                      <span className="text-muted">{point}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <Link to="/register?persona=company" className="btn fw-semibold d-inline-flex align-items-center" style={{ backgroundColor: '#fff', color: '#1E3A5F', border: '1px solid #1E3A5F', textTransform: 'none', fontSize: '1rem', padding: '0.7rem 1.5rem' }}>
                    {t('landing.registerOptions.company.cta')} <i className="feather-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>

            {/* Privatperson */}
            <div className="col-md-6">
              <div className="h-100 bg-white rounded-4 p-4 d-flex flex-column border">
                <i className="bi bi-person-circle mb-3" style={{ color: '#1d6166', fontSize: '1.9rem' }}></i>
                <h3 className="h4 fw-bold mb-2">{t('landing.registerOptions.person.title')}</h3>
                <p className="text-muted mb-3">{t('landing.registerOptions.person.description')}</p>
                <ul className="list-unstyled mb-4">
                  {translationList('landing.registerOptions.person.points').map((point, i) => (
                    <li key={i} className="d-flex align-items-start gap-2 mb-2">
                      <i className="feather-check-circle flex-shrink-0" style={{ color: '#2ECC71', marginTop: '2px' }}></i>
                      <span className="text-muted">{point}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <Link to="/register?persona=person" className="btn fw-semibold d-inline-flex align-items-center" style={{ backgroundColor: '#fff', color: '#1E3A5F', border: '1px solid #1E3A5F', textTransform: 'none', fontSize: '1rem', padding: '0.7rem 1.5rem' }}>
                    {t('landing.registerOptions.person.cta')} <i className="feather-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-5" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #1a2a3a 100%)' }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-11">
              <div className="text-center mb-5">
                <h2 className="text-white fw-bold mb-3" style={{ fontSize: '2.5rem', letterSpacing: '2px' }}>
                  {t('landing.contact.title')}
                </h2>
                <p className="text-white-50 mb-0" style={{ fontSize: '20px' }}>
                  {t('landing.contact.subtitle')}
                </p>
              </div>
              
              <div className="row g-4 align-items-stretch">
                <div className="col-lg-4">
                  <CompanyLegalDetails
                    headingLevel="h3"
                    className="shadow-sm"
                  />
                </div>

                <div className="col-lg-8">
                  <div className="bg-white rounded-4 p-3 p-md-4"><ContactForm /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-4 bg-dark text-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-4">
              <img src="/assets/images/logo-full.png" alt="ETAPPIO" height="30" className="mb-2" style={{ filter: 'brightness(0) invert(1)' }} />
              <p className="text-muted small mb-0">{t('landing.footer.copyright')}</p>
            </div>
            <div className="col-md-8 mt-3 mt-md-0">
              <nav
                aria-label={t('landing.footer.navigationLabel')}
                className="d-flex flex-wrap gap-3 justify-content-md-end"
              >
                <Link to="/integritet" className="text-muted text-nowrap">{t('landing.footer.privacyPolicy')}</Link>
                <Link to="/anvandarvillkor" className="text-muted text-nowrap">{t('landing.footer.termsOfService')}</Link>
                <Link to="/aterbetalning-och-avbokning" className="text-muted text-nowrap">{t('landing.footer.refundPolicy')}</Link>
                <Link to="/leveransinformation" className="text-muted text-nowrap">{t('landing.footer.deliveryInformation')}</Link>
                <Link to="/kontakt" className="text-muted text-nowrap">{t('landing.footer.contact')}</Link>
              </nav>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
